"use client";

import BookCover from "./BookCover";

interface BookCoverLoadingProps {
  title: string;
  src: string;
  /** 0–100. Ignored visually when `indeterminate` is true. */
  percent: number;
  /** True during the import phase, before a real percentage exists. */
  indeterminate: boolean;
  /** Short verb shown under the percentage, e.g. "Translating" / "Preparing". */
  label: string;
}

// Amazon/Kindle-style "downloading" cover: the artwork sits dimmed and
// desaturated, and a full-colour copy fills up from the bottom to the current
// translation percentage, with a shimmer riding the waterline. While the book is
// still importing (no real percent yet) the fill breathes up and down instead.
export default function BookCoverLoading({
  title,
  src,
  percent,
  indeterminate,
  label,
}: BookCoverLoadingProps) {
  // clip-path reveals the bottom `percent%` of the colour layer; insetting the
  // top (100 - percent)% keeps it pixel-aligned with the dimmed base layer.
  const reveal = Math.max(0, Math.min(100, percent));
  const clip = `inset(${100 - reveal}% 0 0 0)`;

  return (
    <div className="relative h-full w-full overflow-hidden bg-app-tinted">
      {/* Dimmed, desaturated base — the "not yet downloaded" state. */}
      <div className="absolute inset-0 opacity-50 grayscale brightness-95">
        <BookCover src={src} title={title} />
      </div>

      {/* Full-colour layer, clipped to the filled portion. */}
      <div
        className={`absolute inset-0 ${indeterminate ? "animate-lumi-fill-breathe" : ""}`}
        style={
          indeterminate
            ? undefined
            : { clipPath: clip, WebkitClipPath: clip, transition: "clip-path 0.6s ease-out" }
        }
      >
        <BookCover src={src} title={title} />
      </div>

      {/* Waterline shimmer. Pinned to the top of the filled portion when we have
          a real percent; for the indeterminate phase it rides near the middle. */}
      <div
        className="pointer-events-none absolute inset-x-0 h-8 overflow-hidden"
        style={{
          bottom: indeterminate ? "40%" : `calc(${reveal}% - 1rem)`,
          transition: indeterminate ? undefined : "bottom 0.6s ease-out",
        }}
      >
        <div className="h-full w-1/2 animate-lumi-shimmer bg-gradient-to-r from-transparent via-white/70 to-transparent" />
      </div>

      {/* Status label. */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-app-text to-transparent px-2 pb-2 pt-6">
        <span className="rounded-full bg-app-surface px-2.5 py-1 text-xs font-semibold text-app-text shadow-sm">
          {indeterminate ? `${label}…` : `${reveal}% · ${label}`}
        </span>
      </div>
    </div>
  );
}
